import { Constants } from "./constants";
import { TitleTrackerId } from "./title-trackers";

/**
 * Manual run entry catalogue — option lists and validation rules for the
 * "Add Manual Run" forms in the Ribbon Room. Used by web and mobile so both
 * apps offer exactly the same choices and validate identically.
 *
 * NOTE on result values: every tracker uses `Constants.run_result_status`
 * ("Q" / "NQ" / ...) EXCEPT BHA Barn Hunt, whose import path stores
 * `Constants.score_result_type` ("qualified" / "not_qualified" / "absent").
 * That is what the server expects on the wire — do not "fix" it here.
 */

export interface IManualRunOption {
  value: string;
  label: string;
}

/** Shape of the values a manual-run form holds (strings straight from inputs). */
export interface IManualRunFormValues {
  trial_date?: Date | string | null;
  level?: string;
  element?: string;
  division?: string;
  class?: string;
  section?: string;
  result_status?: string;
  score?: string | number;
  time?: string;
  time_seconds?: string | number;
  height_at_withers?: string | number;
  placement?: string | number;
  dogs_defeated?: string | number;
  awards?: string[];
  judge_number?: string;
  judge_name?: string;
  event_name?: string;
  city?: string;
  state?: string;
  notes?: string;
}

export type ManualRunFieldErrors = Partial<Record<keyof IManualRunFormValues, string>>;

export const MANUAL_RUN_LIMITS = {
  notes_max_length: 500,
  state_max_length: 2,
  fast_cat_height_min: 1,
  fast_cat_height_max: 40,
  obedience_score_min: 0,
  obedience_score_max: 200,
  obedience_score_step: 0.5,
  obedience_qualifying_score: 170,
  rally_score_min: 0,
  rally_score_max: 100,
  rally_score_step: 0.5,
  ukc_rally_score_min: 0,
  ukc_rally_score_max: 200,
  ukc_rally_score_step: 0.5,
  /** Barn Hunt run time: MM:SS or M:SS with optional hundredths (e.g. 2:30 or 02:30.50) */
  barn_hunt_time_pattern: /^\d{1,2}:\d{2}(\.\d{1,2})?$/,
} as const;

const RESULT_Q_NQ_ABS_DQ_EXC: IManualRunOption[] = [
  { value: Constants.run_result_status.qualified, label: "Q - Qualified" },
  { value: Constants.run_result_status.not_qualified, label: "NQ - Not Qualified" },
  { value: Constants.run_result_status.absent, label: "ABS - Absent" },
  { value: Constants.run_result_status.disqualified, label: "DQ - Disqualified" },
  { value: Constants.run_result_status.excused, label: "EXC - Excused" },
];

const RESULT_Q_NQ_ABS: IManualRunOption[] = [
  { value: Constants.run_result_status.qualified, label: "Q (Qualified)" },
  { value: Constants.run_result_status.not_qualified, label: "NQ (Not Qualified)" },
  { value: Constants.run_result_status.absent, label: "ABS (Absent)" },
];

const RESULT_Q_NQ: IManualRunOption[] = [
  { value: Constants.run_result_status.qualified, label: "Qualified" },
  { value: Constants.run_result_status.not_qualified, label: "Not Qualified" },
];

export const MANUAL_RUN_OPTIONS = {
  ukc_nosework: {
    levels: ["Novice", "Advanced", "Superior", "Master", "Elite"].map((v) => ({ value: v, label: v })),
    /** Handler Discrimination has its own level ladder */
    handler_discrimination_levels: ["Novice", "Advanced", "Excellent", "Master"].map((v) => ({ value: v, label: v })),
    elements: ["Container", "Exterior", "Interior", "Vehicle", "Handler Discrimination"].map((v) => ({ value: v, label: v })),
    divisions: [
      { value: "A", label: "A" },
      { value: "B", label: "B" },
    ],
    results: RESULT_Q_NQ_ABS_DQ_EXC,
    handler_discrimination_element: "Handler Discrimination",
    defaults: { level: "Novice", element: "Container", division: "A", result_status: Constants.run_result_status.qualified },
    help: {
      division: "A: Handler has not earned a title in this level\nB: Handler has earned a title in this level",
    },
  },
  akc_scent_work: {
    levels: ["Novice A", "Novice B", "Advanced", "Excellent", "Master", "Detective"].map((v) => ({ value: v, label: v })),
    elements: ["Container", "Interior", "Exterior", "Buried", "Handler Discrimination", "Detective"].map((v) => ({ value: v, label: v })),
    results: RESULT_Q_NQ_ABS_DQ_EXC,
    detective: "Detective",
    defaults: { level: "Novice A", element: "Container", result_status: Constants.run_result_status.qualified },
    help: {
      detective: 'Note: Detective is a separate class - level and element must both be "Detective"',
    },
  },
  akc_fetch: {
    levels: [
      { value: Constants.dog_class_element_level.fetch_novice, label: "Novice" },
      { value: Constants.dog_class_element_level.fetch_intermediate, label: "Intermediate" },
      { value: Constants.dog_class_element_level.fetch_advanced, label: "Advanced" },
      { value: Constants.dog_class_element_level.fetch_retriever, label: "Retriever" },
    ],
    results: RESULT_Q_NQ_ABS,
    help: {
      judge_number: "Preferred for uniqueness (e.g., AKC judge number)",
      judge_name: "Used as fallback if judge number not available",
    },
  },
  akc_obedience: {
    classes: ["Novice A", "Novice B", "Open A", "Open B", "Utility A", "Utility B"].map((v) => ({ value: v, label: v })),
    results: RESULT_Q_NQ_ABS,
    help: {
      score: "Minimum 170 required for qualification. Half points allowed (e.g., 185.5)",
      judge_number: "Preferred for uniqueness (e.g., AKC judge number)",
      judge_name: "Used as fallback if judge number not available",
    },
  },
  akc_rally: {
    classes: ["Novice A", "Novice B", "Intermediate", "Advanced A", "Advanced B", "Excellent A", "Excellent B", "Master"].map((v) => ({ value: v, label: v })),
    results: RESULT_Q_NQ,
    help: {
      score: "Half points allowed (e.g., 85.5)",
    },
  },
  ukc_obedience: {
    classes: ["Novice", "Open A", "Open B", "Utility A", "Utility B"].map((v) => ({ value: v, label: v })),
    results: RESULT_Q_NQ_ABS,
    awards: [
      { value: Constants.ukc_obedience_award.high_in_trial, label: "High in Trial (HIT)" },
      { value: Constants.ukc_obedience_award.high_combined, label: "High Combined (HC)" },
    ],
    help: {
      score: "Minimum 170 required for qualification. Half points allowed (e.g., 185.5)",
      placement: "Required for championship points",
      dogs_defeated: "Required for championship points",
      awards: "Required for championship titles (UOCH, UGOCH)",
      judge_number: "Preferred for uniqueness",
      judge_name: "Used as fallback if judge number not available",
    },
  },
  ukc_rally: {
    classes: [
      { value: Constants.ukc_rally_class.level_1_a, label: "Level 1 A" },
      { value: Constants.ukc_rally_class.level_1_b, label: "Level 1 B" },
      { value: Constants.ukc_rally_class.level_2_a, label: "Level 2 A" },
      { value: Constants.ukc_rally_class.level_2_b, label: "Level 2 B" },
      { value: Constants.ukc_rally_class.level_3_a, label: "Level 3 A" },
      { value: Constants.ukc_rally_class.level_3_b, label: "Level 3 B" },
      { value: Constants.ukc_rally_class.masters_a, label: "Masters A" },
      { value: Constants.ukc_rally_class.masters_b, label: "Masters B" },
    ],
    results: RESULT_Q_NQ,
    awards: [{ value: Constants.ukc_obedience_award.high_in_trial, label: "High in Trial (HIT)" }],
    defaults: { result_status: Constants.run_result_status.qualified },
    help: {
      score: "Half points allowed (e.g., 185.5)",
      placement: "1st, 2nd, etc.",
      dogs_defeated: "For championship points",
    },
  },
  bha_barn_hunt: {
    levels: [
      { value: Constants.dog_class_element_level.barn_hunt_instinct, label: "Instinct" },
      { value: Constants.dog_class_element_level.barn_hunt_novice, label: "Novice" },
      { value: Constants.dog_class_element_level.barn_hunt_open, label: "Open" },
      { value: Constants.dog_class_element_level.barn_hunt_senior, label: "Senior" },
      { value: Constants.dog_class_element_level.barn_hunt_master, label: "Master" },
      { value: Constants.dog_class_element_level.barn_hunt_crazy_8s, label: "Crazy 8s" },
      { value: Constants.dog_class_element_level.barn_hunt_line_drive, label: "Line Drive" },
    ],
    sections: [
      { value: "A", label: "A" },
      { value: "B", label: "B" },
    ],
    /** Barn Hunt stores score_result_type values — see note at top of file. */
    results: [
      { value: Constants.score_result_type.qualified, label: "Q (Qualified)" },
      { value: Constants.score_result_type.not_qualified, label: "NQ (Not Qualified)" },
      { value: Constants.score_result_type.absent, label: "ABS (Absent)" },
    ],
    defaults: { section: "A" },
    help: {
      section: "A = First attempt at level, B = Repeat runs for X-level titles",
      time: "Format: MM:SS or M:SS (e.g., 2:30 or 02:30.50)",
    },
  },
} as const;

/** Barn Hunt level → display label (used by run rows). */
export const BARN_HUNT_LEVEL_LABELS: Record<string, string> = MANUAL_RUN_OPTIONS.bha_barn_hunt.levels.reduce(
  (acc, l) => ({ ...acc, [l.value]: l.label }),
  {} as Record<string, string>
);

/** Barn Hunt score_result_type → display label (used by run rows). */
export const BARN_HUNT_RESULT_LABELS: Record<string, string> = {
  [Constants.score_result_type.qualified]: "Qualified",
  [Constants.score_result_type.not_qualified]: "NQ",
  [Constants.score_result_type.absent]: "Absent",
};

const toNumber = (v: string | number | undefined | null): number | undefined => {
  if (v === undefined || v === null || v === "") return undefined;
  const n = typeof v === "number" ? v : parseFloat(v);
  return isNaN(n) ? undefined : n;
};

const isHalfPointStep = (n: number): boolean => Math.abs(n * 2 - Math.round(n * 2)) < 1e-9;

const endOfToday = (): Date => {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d;
};

/**
 * Validate manual-run form values for a tracker. Returns a map of field → message
 * (empty object when valid). Mirrors the checks the web forms perform today,
 * plus the Barn Hunt "time required for Crazy 8s" rule the web only enforced via
 * the HTML `required` attribute.
 */
export const validateManualRun = (
  trackerId: TitleTrackerId,
  values: IManualRunFormValues
): ManualRunFieldErrors => {
  const errors: ManualRunFieldErrors = {};

  // Common: trial date
  if (!values.trial_date) {
    errors.trial_date = "Trial date is required";
  } else {
    const d = new Date(values.trial_date);
    if (isNaN(d.getTime())) {
      errors.trial_date = "Trial date is invalid";
    } else if (d > endOfToday()) {
      errors.trial_date = "Trial date cannot be in the future";
    }
  }

  // Common: optional text limits
  if (values.notes && values.notes.length > MANUAL_RUN_LIMITS.notes_max_length) {
    errors.notes = `Notes must be ${MANUAL_RUN_LIMITS.notes_max_length} characters or fewer`;
  }
  if (values.state && values.state.trim().length > MANUAL_RUN_LIMITS.state_max_length) {
    errors.state = "Use the 2-letter state code";
  }

  const requireResult = () => {
    if (!values.result_status) errors.result_status = "Result status is required";
  };

  const requireScore = (min: number, max: number) => {
    const score = toNumber(values.score);
    if (score === undefined) {
      errors.score = "Score is required";
    } else if (score < min || score > max) {
      errors.score = `Score must be between ${min} and ${max}`;
    } else if (!isHalfPointStep(score)) {
      errors.score = "Score must be in half-point increments";
    }
  };

  const validatePlacementAndDefeated = () => {
    const placement = toNumber(values.placement);
    if (values.placement !== undefined && values.placement !== "" && (placement === undefined || placement < 1 || !Number.isInteger(placement))) {
      errors.placement = "Placement must be a whole number of 1 or more";
    }
    const defeated = toNumber(values.dogs_defeated);
    if (values.dogs_defeated !== undefined && values.dogs_defeated !== "" && (defeated === undefined || defeated < 0 || !Number.isInteger(defeated))) {
      errors.dogs_defeated = "Dogs defeated must be a whole number of 0 or more";
    }
  };

  switch (trackerId) {
    case "akc_fast_cat": {
      const time = toNumber(values.time_seconds);
      if (time === undefined || time <= 0) {
        errors.time_seconds = "Valid run time is required";
      }
      const height = toNumber(values.height_at_withers);
      if (
        height === undefined ||
        height < MANUAL_RUN_LIMITS.fast_cat_height_min ||
        height > MANUAL_RUN_LIMITS.fast_cat_height_max
      ) {
        errors.height_at_withers = `Height must be between ${MANUAL_RUN_LIMITS.fast_cat_height_min} and ${MANUAL_RUN_LIMITS.fast_cat_height_max} inches`;
      }
      break;
    }

    case "ukc_nosework": {
      if (!values.level) errors.level = "Level is required";
      if (!values.element) errors.element = "Element is required";
      if (!values.division) errors.division = "Division is required";
      requireResult();
      break;
    }

    case "akc_scent_work": {
      if (!values.level) errors.level = "Level is required";
      if (!values.element) errors.element = "Element is required";
      const detective = MANUAL_RUN_OPTIONS.akc_scent_work.detective;
      if (values.element === detective && values.level !== detective) {
        errors.element = "Detective element can only be used with Detective level";
      }
      if (values.level === detective && values.element !== detective) {
        errors.level = "Detective level can only be used with Detective element";
      }
      requireResult();
      break;
    }

    case "akc_fetch": {
      if (!values.level) errors.level = "Level is required";
      requireResult();
      break;
    }

    case "akc_obedience": {
      if (!values.class) errors.class = "Class is required";
      requireScore(MANUAL_RUN_LIMITS.obedience_score_min, MANUAL_RUN_LIMITS.obedience_score_max);
      requireResult();
      break;
    }

    case "akc_rally": {
      if (!values.class) errors.class = "Class is required";
      requireScore(MANUAL_RUN_LIMITS.rally_score_min, MANUAL_RUN_LIMITS.rally_score_max);
      requireResult();
      break;
    }

    case "ukc_obedience": {
      if (!values.class) errors.class = "Class is required";
      requireScore(MANUAL_RUN_LIMITS.obedience_score_min, MANUAL_RUN_LIMITS.obedience_score_max);
      requireResult();
      validatePlacementAndDefeated();
      break;
    }

    case "ukc_rally": {
      if (!values.class) errors.class = "Class is required";
      requireScore(MANUAL_RUN_LIMITS.ukc_rally_score_min, MANUAL_RUN_LIMITS.ukc_rally_score_max);
      requireResult();
      validatePlacementAndDefeated();
      break;
    }

    case "bha_barn_hunt": {
      if (!values.level) errors.level = "Level is required";
      if (!values.section) errors.section = "Section is required";
      requireResult();
      const isCrazy8s = values.level === Constants.dog_class_element_level.barn_hunt_crazy_8s;
      if (isCrazy8s && !values.time) {
        errors.time = "Time is required for Crazy 8s";
      } else if (values.time && !MANUAL_RUN_LIMITS.barn_hunt_time_pattern.test(values.time)) {
        errors.time = "Time must be MM:SS or M:SS (e.g., 2:30)";
      }
      break;
    }
  }

  return errors;
};
